// Vendor Imports
import _ from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';

// Project Imports
import { FilterFooter } from '../FilterFooter';
import * as CheckboxFilter from '../lib/Filters/CheckboxFilter';
import { I18n } from 'common/i18n';
import { addPopupListener } from './InputFocus';
import { FilterColumnsMap, FilterEditorProps } from '../types';
import { FilterValue, FILTER_FUNCTION, SoqlFilter, BinaryOperator, NoopFilter } from '../SoqlFilter';
import { PicklistOption } from 'common/components/Picklist';
import * as BaseFilter from '../lib/Filters/BaseFilter';
import { ForgeCheckbox, ForgeDivider, ForgeList, ForgeListItem } from '@tylertech/forge-react';

export interface CheckboxFilterEditorProps extends FilterEditorProps {
  appliedFilter: BinaryOperator | NoopFilter;
  columns: FilterColumnsMap;
}

interface CheckboxFilterEditorState {
  value: string;
  selectedValues: FilterValue[];
  updatedFilter: SoqlFilter;
}

export class CheckboxFilterEditor extends Component<CheckboxFilterEditorProps, CheckboxFilterEditorState> {
  checkboxFilter: HTMLDivElement;
  removePopupListener = () => {};
  popupListenerRemoved = false;

  constructor(props: CheckboxFilterEditorProps) {
    super(props);

    _.bindAll(this, [
      'applyFilter',
      'booleanToLocalizedString',
      'booleanToString',
      'isDirty',
      'onChange',
      'resetFilter',
      'stringToBoolean',
      'updateSelectedValues',
    ]);

    // get the initial state from props
    const initialState = this.getInitialState(props);

    this.state = initialState;
  }

  componentDidMount() {
    const { popupRef } = this.props;
    this.removePopupListener = addPopupListener(popupRef, this.checkboxFilter, () => {
      this.popupListenerRemoved = true;
    });
  }

  componentWillUnmount() {
    const { popupRef } = this.props;
    if (!this.popupListenerRemoved) {
      popupRef?.current?.removeEventListener('forge-popover-position', this.removePopupListener);
    }
  }

  UNSAFE_componentWillUpdate(nextProps: Readonly<CheckboxFilterEditorProps>): void {
    if (!isEqual(nextProps.appliedFilter, this.props.appliedFilter)) {
      this.setState(this.getInitialState(nextProps));
    }
  }

  getInitialState(props: CheckboxFilterEditorProps) {
    const { appliedFilter } = props;
    let selectedValues: FilterValue[] = [];

    if (appliedFilter.function === FILTER_FUNCTION.BINARY_OPERATOR) {
      selectedValues = _.map(appliedFilter.arguments, (argument) => {
        if (_.includes(['IS NULL', 'IS NOT NULL'], argument.operator)) {
          return null;
        }

        return argument.operand;
      });
    }

    return {
      selectedValues,
      value: '',
      updatedFilter: appliedFilter,
    };
  }

  onChange(option: PicklistOption, checkedState: boolean) {
    const value = this.stringToBoolean(option.value);
    if (checkedState) {
      this.updateSelectedValues(_.union(this.state.selectedValues, [value]));
    } else {
      this.updateSelectedValues(_.without(this.state.selectedValues, value));
    }
  }

  booleanToLocalizedString(value: FilterValue) {
    if (value === true) {
      return I18n.t('shared.components.filter_bar.checkbox_filter.true_value');
    } else if (value === false) {
      return I18n.t('shared.components.filter_bar.checkbox_filter.false_value');
    } else if (this.props.showNullsAsFalse) {
      return I18n.t('shared.components.filter_bar.checkbox_filter.false_value');
    } else {
      return I18n.t('shared.components.filter_bar.checkbox_filter.no_value');
    }
  }

  booleanToString(value: FilterValue) {
    if (value === true) {
      return 'true';
    } else if (value === false) {
      return 'false';
    } else {
      return null;
    }
  }

  stringToBoolean(value: string) {
    if (value === 'true') {
      return true;
    } else if (value === 'false') {
      return false;
    } else {
      return null;
    }
  }

  resetFilter() {
    const { appliedFilter, columns, onUpdate } = this.props;

    this.updateSelectedValues([]);

    if (this.state.value !== '') {
      this.setState({ value: '' });
    }

    const resetFilter = BaseFilter.reset(appliedFilter, columns);
    onUpdate(resetFilter);
  }

  applyFilter() {
    const { appliedFilter, columns, onUpdate } = this.props;
    const { selectedValues } = this.state;
    const updatedFilter = CheckboxFilter.getCheckboxFilter(appliedFilter, columns, selectedValues);
    onUpdate(updatedFilter);
    this.setState({ updatedFilter: updatedFilter });
  }

  updateSelectedValues(nextSelectedValues: FilterValue[]) {
    this.setState({
      selectedValues: _.uniq(nextSelectedValues),
    });
  }

  isDirty() {
    const { appliedFilter, columns } = this.props;
    const { selectedValues, updatedFilter } = this.state;

    return !_.isEqual(
      CheckboxFilter.getCheckboxFilter(appliedFilter, columns, selectedValues),
      updatedFilter,
    );
  }

  render() {
    const { appliedFilter, isReadOnly, onRemove, showNullsAsFalse, showRemoveButtonInFooter } = this.props;
    const { selectedValues } = this.state;

    const possibleOptions: PicklistOption[] = showNullsAsFalse
      ? [
          {
            // null as false option
            title: I18n.t('shared.components.filter_bar.checkbox_filter.false_value'),
            value: null,
          },
          {
            // true option
            title: I18n.t('shared.components.filter_bar.checkbox_filter.true_value'),
            value: 'true',
          },
        ]
      : [
          {
            // null option
            title: I18n.t('shared.components.filter_bar.checkbox_filter.no_value'),
            value: null,
          },
          {
            // true option
            title: I18n.t('shared.components.filter_bar.checkbox_filter.true_value'),
            value: 'true',
          },
          {
            // false option
            title: I18n.t('shared.components.filter_bar.checkbox_filter.false_value'),
            value: 'false',
          },
        ];

    const selectedOptions = _.map(selectedValues, (selectedValue) => {
      return {
        title: this.booleanToLocalizedString(selectedValue),
        value: this.booleanToString(selectedValue),
      };
    });

    const footerAttributes = {
      disableApplyFilter: !this.isDirty(),
      isDrilldown: appliedFilter.isDrilldown,
      isReadOnly,
      onClickApply: this.applyFilter,
      onClickRemove: onRemove,
      onClickReset: this.resetFilter,
      showRemoveButton: showRemoveButtonInFooter,
    };

    const listItems = possibleOptions.map((option) => (
      <ForgeListItem key={`option-${option.title}`} value={option.value}>
        <ForgeCheckbox
          slot="start"
          checked={selectedOptions.some((selectedOption) => selectedOption.title === option.title)}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            this.onChange(option, event.target.checked)
          }
          data-testid={`${option.title}-checkbox-filter-option`}
        ></ForgeCheckbox>
        <span>{option.title}</span>
      </ForgeListItem>
    ));

    return (
      <div
        className="filter-controls checkbox-filter"
        ref={(el: HTMLDivElement) => (this.checkboxFilter = el)}
      >
        <div className="checkbox-filter-container">
          <ForgeList dense>{listItems}</ForgeList>
          <ForgeDivider></ForgeDivider>
        </div>
        <FilterFooter {...footerAttributes} />
      </div>
    );
  }
}
